
import Locale from './Locale.js'
import ListFormat from './ListFormat.js'
import NumberingSystem from './NumberingSystem.js'
import { getOption, getArrayOption, isObject, resolveLocale, getCanonicalLocales } from './utils.js'
import { numberingSystemNames } from './data/numberingSystems.js'
import SlotMap from '@neumatter/slot-map'
import supportedLocales from './data/supportedLocales.js'

const DURATION_FIELDS_MAP = new Map([
  ['years', 0],
  ['months', 1],
  ['weeks', 2],
  ['days', 3],
  ['hours', 4],
  ['minutes', 5],
  ['seconds', 6],
  ['milliseconds', 7],
  ['microseconds', 8],
  ['nanoseconds', 9]
])

const DURATION_FIELDS = [
  'years',
  'months',
  'weeks',
  'days',
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
  'microseconds',
  'nanoseconds'
]
const DURATION_FORMAT_TABLE = [
  [
    'years',
    'year',
    ['long', 'short', 'narrow'],
    'short'
  ],
  [
    'months',
    'month',
    ['long', 'short', 'narrow'],
    'short'
  ],
  [
    'weeks',
    'week',
    ['long', 'short', 'narrow'],
    'short'
  ],
  [
    'days',
    'day',
    ['long', 'short', 'narrow'],
    'short'
  ],
  [
    'hours',
    'hour',
    ['long', 'short', 'narrow', 'numeric', '2-digit'],
    'numeric'
  ],
  [
    'minutes',
    'minute',
    ['long', 'short', 'narrow', 'numeric', '2-digit'],
    'numeric'
  ],
  [
    'seconds',
    'second',
    ['long', 'short', 'narrow', 'numeric', '2-digit'],
    'numeric'
  ],
  [
    'milliseconds',
    'millisecond',
    ['long', 'short', 'narrow', 'numeric', '2-digit'],
    'numeric'
  ],
  [
    'microseconds',
    'microsecond',
    ['long', 'short', 'narrow', 'numeric', '2-digit'],
    'numeric'
  ],
  [
    'nanoseconds',
    'nanosecond',
    ['long', 'short', 'narrow', 'numeric', '2-digit'],
    'numeric'
  ]
]

function getNumberOption (options, field, min, max, fallback) {
  if (typeof options !== 'object') {
    throw new RangeError('options must be an object')
  }

  const value = options[field]

  if (value === undefined) {
    return fallback
  }

  if (value < min) {
    return min
  }

  if (value > max) {
    return max
  }

  return value
}

export default class DurationFormat {
  static supportedLocalesOf (locales, options) {
    return Locale.getCanonicalLocales(locales)
  }

  #fields

  constructor (locales, options) {
    options = options || {}
    const opts = Object.create(null)
    const requestedLocales = getCanonicalLocales(locales)
    const [localeData, availableLocales] = loadDataLazy()

    const localeMatcher = getOption(
      options,
      'localeMatcher',
      'string',
      ['best fit', 'lookup'],
      'best fit'
    )

    const numberingSystem = getOption(
      options,
      'numberingSystem',
      'string',
      undefined,
      undefined
    )

    if (
      numberingSystem !== undefined &&
      numberingSystemNames.indexOf(numberingSystem) < 0
    ) {
      // 7.a. If numberingSystem does not match the Unicode Locale Identifier type nonterminal,
      // throw a RangeError exception.
      throw RangeError(`Invalid numberingSystems: ${numberingSystem}`)
    }

    opts.localeMatcher = localeMatcher
    opts.nu = numberingSystem
    // const r = Locale.resolve(requestedLocales, opts)
    const r = resolveLocale(
      availableLocales,
      requestedLocales,
      opts,
      // [[RelevantExtensionKeys]] slot, which is a constant
      [],
      localeData,
      () => Locale.systemLocale
    )

    const style = getOption(
      options,
      'style',
      'string',
      ['long', 'short', 'narrow', 'digital'],
      'short'
    )

    this.#fields = getArrayOption(
      options,
      'fields',
      DURATION_FIELDS,
      DURATION_FIELDS
    )

    const internalSlots = SlotMap.get(this)
    internalSlots.set('locale', r.locale)
    let prevStyle = ''

    for (const field of this.#fields) {
      const tableIndex = DURATION_FIELDS_MAP.get(field)
      const row = DURATION_FORMAT_TABLE[tableIndex]
      const unit = row[0]
      const valueList = row[2]
      const digitalBase = row[3]
      const unitOptions = getDurationUnitOptions(
        unit,
        options,
        style,
        valueList,
        digitalBase,
        prevStyle
      )

      internalSlots.set(unit, unitOptions.style)
      internalSlots.set(unit + 'Display', unitOptions.display)

      if (isTime(unit)) {
        prevStyle = unitOptions.style
      }
    }

    internalSlots.set('fractionalDigits', getNumberOption(options, 'fractionalDigits', 0, 9, 0))
  }

  format (duration) {
    let result = ''
    const parts = partitionDurationFormatPattern(this, duration)

    for (const part of parts) {
      result += part.value
    }

    return result
  }

  formatToParts (duration) {
    return partitionDurationFormatPattern(this, duration)
  }

  resolvedOptions () {
    return SlotMap.get(this).createPublicRecord([
      'locale',
      ...DURATION_FIELDS,
      ...DURATION_FIELDS.map(field => field + 'Display')
    ])
  }
}

function loadDataLazy () {
  let data = SlotMap.getInternal(DurationFormat, 'localeData')
  let availableLocales = SlotMap.getInternal(DurationFormat, 'availableLocales')

  if (availableLocales === undefined || data === undefined) {
    availableLocales = new Set(supportedLocales)
    SlotMap.setInternal(DurationFormat, 'availableLocales', availableLocales)
    data = supportedLocales.reduce((obj, locale) => {
      obj[locale] = {}
      return obj
    }, {})
    SlotMap.setInternal(DurationFormat, 'localeData', data)
  }

  return [data, availableLocales]
}

function getDurationUnitOptions (
  unit,
  options,
  baseStyle,
  stylesList,
  digitalBase,
  prevStyle
) {
  let displayDefault = 'always'

  let style = getOption(
    options,
    unit,
    'string',
    stylesList,
    undefined
  )

  if (style === undefined) {
    if (baseStyle === 'digital') {
      if (!isDigitalAlways(unit)) {
        displayDefault = 'auto'
      }

      style = digitalBase
    } else {
      displayDefault = 'auto'
      style = prevStyle === 'numeric' || prevStyle === '2-digit'
        ? 'numeric'
        : baseStyle
    }
  }

  const displayField = `${unit}Display`

  const display = getOption(
    options,
    displayField,
    'string',
    ['auto', 'always'],
    displayDefault
  )

  if (prevStyle === 'numeric' || prevStyle === '2-digit') {
    if (style !== undefined && (style !== 'numeric' && style !== '2-digit')) {
      throw new RangeError('cannot mix numeric styles and word format styles.')
    } else if (unit === 'minutes' || unit === 'seconds') {
      style = '2-digit'
    }
  }

  return {
    style,
    display
  }
}

function partitionDurationFormatPattern (durationFormat, duration) {
  let result = []
  let done = false
  let separated = false
  const slotMap = SlotMap.get(durationFormat)
  /** @type {NumberingSystem | undefined} */
  let numberingSystem = slotMap.get('numberingSystem')

  while (!done) {
    for (const [unit, numberFormatUnit] of DURATION_FORMAT_TABLE) {
      const style = slotMap.get(unit)
      const display = slotMap.get(unit + 'Display')
      let value = duration[unit]
      const nfOpts = Object.create(null)
      let nextStyle
      if (unit === 'seconds' || unit === 'milliseconds' || unit === 'microseconds') {
        if (unit === 'seconds') {
          nextStyle = slotMap.get('milliseconds')
        } else if (unit === 'milliseconds') {
          nextStyle = slotMap.get('microseconds')
        } else {
          nextStyle = slotMap.get('nanoseconds')
        }

        if (nextStyle === 'numeric') {
          if (unit === 'seconds') {
            value = value + duration.milliseconds / 1e3 + duration.microseconds / 1e6 + duration.nanoseconds / 1e9
          } else if (unit === 'milliseconds') {
            value = value + duration.microseconds / 1e3 + duration.nanoseconds / 1e6
          } else {
            value = value + duration.nanoseconds / 1e3
          }
          const fractionalDigits = SlotMap.getInternal(durationFormat, 'fractionalDigits')
          nfOpts.maximumFractionDigits = fractionalDigits
          nfOpts.minimumFractionDigits = fractionalDigits
          nfOpts.roundingMode = 'trunc'
          done = true
        }
      }

      if (style === '2-digit') {
        nfOpts.minimumIntegerDigits = 2
      }

      if (value !== 0 || display !== 'auto') {
        if (style === '2-digit' || style === 'numeric') {
          const nf = new Intl.NumberFormat(slotMap.get('locale'), nfOpts)
          if (!separated) {
            result.push([])
          } else {
            separated = false
          }
          const last = result[result.length - 1]
          const parts = nf.formatToParts(value)

          for (const part of parts) { // 8.
            last.push({ type: part.type, value: part.value, unit: numberFormatUnit })
          }

          let nextValue, nextDisplay
          // 9.
          if (unit === 'hours' || unit === 'minutes') {
            // 9.a
            if (unit === 'hours') {
              nextValue = duration.minutes
              nextDisplay = slotMap.get('minutesDisplay')
            } else {
              nextValue = duration.seconds
              nextDisplay = slotMap.get('secondsDisplay')
              if (slotMap.get('milliseconds') === 'numeric') {
                nextValue = nextValue + duration.milliseconds / 1e3 + duration.microseconds / 1e6 + duration.nanoseconds / 1e9
              }
            }

            if (nextValue !== 0 || nextDisplay !== 'auto') {
              if (numberingSystem === undefined) {
                numberingSystem = new NumberingSystem(slotMap.get('locale'))
                slotMap.set('numberingSystem', numberingSystem)
              }

              last.push({ type: 'literal', value: numberingSystem.timeSeparator })
              separated = true
            }
          }
        } else if (unit !== 'microseconds' && unit !== 'nanoseconds') { // K.1.ii
          nfOpts.style = 'unit'
          nfOpts.unit = numberFormatUnit
          nfOpts.unitDisplay = style
          const nf = new Intl.NumberFormat(slotMap.get('locale'), nfOpts)
          const list = []
          const parts = nf.formatToParts(value)
          for (const part of parts) { // 8.
            list.push({ type: part.type, value: part.value, unit: numberFormatUnit })
          }
          result.push(list)
        }
      }

      if (done) {
        break
      }
    }

    done = true
  }

  const lfOpts = Object.create(null)
  lfOpts.type = 'unit'
  let listStyle = slotMap.get('style')

  if (listStyle === 'digital') {
    listStyle = 'short'
  }

  lfOpts.style = listStyle
  const lf = new ListFormat(slotMap.get('locale'), lfOpts)
  const createPartsFromList = SlotMap.getInternal(ListFormat, 'createPartsFromList')
  const internal = SlotMap.getInternal(lf, 'internal')
  const parts = createPartsFromList(internal, result)

  if (!Array.isArray(parts)) {
    result = [parts]
  } else {
    result = []
    for (const part of parts) {
      result.push({ ...part })
    }
  }

  const flattened = []

  for (const part of result) {
    if (part.type === 'literal') {
      flattened.push(part)
    } else {
      if (isObject(part.value) && 'type' in part.value && 'value' in part.value && 'unit' in part.value) {
        flattened.push(part.value)
      } else {
        for (const element of part.value) {
          flattened.push(element)
        }
      }
    }
  }

  return flattened
}

function isDigitalAlways (unit) {
  const index = DURATION_FIELDS_MAP.get(unit)
  return index > 3 && index < 7
}

function isTime (unit) {
  const index = DURATION_FIELDS_MAP.get(unit)
  return index > 3
}

// console.log(new Locale('en-US-u-tz-uschi'))
